import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'
import { TokenManager } from '@/utils/token'
import { UserStorage } from '@/utils/user-storage'
import { getMenuPermissions, type MenuPermission } from '@/api/menu-permission'
import { getCurrentUserRolePrivileges } from '@/api/permission'
import { getAppType, isDevelopment, isDevFullMenu,isClient } from '@/utils/app-config'
import { resetShokzFeatureCache } from '@/composables/useShokzFeature'
import { resetUnifiedOrderVersionConfigCache } from '@/composables/useUnifiedOrderVersion'
import { buildHowxmIdentifyExtras, syncHowxmIdentify } from '@/utils/howxm'
import { resetTracking, syncTrackingIdentify } from '@/tracking'

import {
  clientResetPassword,
  clientSendLoginCode,
  clientSendPasswordResetCode,
  clientSendRegisterCode,
  clientUserLogin,
  clientUserLoginVerify,
  clientUserRegister,
  convertCodeToToken,
  type ConvertCodeToTokenResponse,
  convertTokenToCode,
  getClientUserDetail,
  getTenantUserDetail,
  resetPassword,
  sendLoginCode,
  sendPasswordResetCode,
  sendRegisterCode,
  tenantUserLogin,
  userLogin,
  userLoginVerify,
  userLogout,
  userRegister
} from '@/api/auth'
import type {
  Enterprise,
  LoginCodeRequest,
  LoginRequest,
  LoginVerifyRequest,
  PasswordResetCodeRequest,
  PasswordResetRequest,
  RegisterCodeRequest,
  RegisterRequest
} from '@/api/types/auth'
import { UserType } from '@/enums/auth'
import { t } from '@/i18n'

/**
 * 用户信息接口
 */
interface User {
  id: string
  username: string
  email: string
  phone: string
  realName: string
  tenantId: string
  tenantName?: string
  branchId?: string
  branchName?: string
  enterpriseId: string
  userType: number
  roleType: number
}

/**
 * 登录凭据接口
 */
interface LoginCredentials {
  account: string
  password: string
  enterpriseId: string
}

/**
 * 认证状态管理Store
 *
 * 存储机制说明：
 * - 所有用户信息、token等状态都按照应用类型（VITE_APP_TYPE）进行隔离存储
 * - localStorage key格式：ct_auth_store_{appType} (如：ct_auth_store_client, ct_auth_store_admin)
 * - Cookie token key格式：ct_token_{appType} (如：ct_token_client, ct_token_admin)
 * - 这样确保不同身份的用户信息不会相互干扰
 *
 * @example
 * // 客户端环境 (VITE_APP_TYPE=client)
 * // localStorage: ct_auth_store_client
 * // Cookie: ct_token_client
 *
 * // 运营端环境 (VITE_APP_TYPE=admin)
 * // localStorage: ct_auth_store_admin
 * // Cookie: ct_token_admin
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    // 根据应用类型创建专用的token管理器
    const appType = getAppType()
    const tokenManager = new TokenManager(`ct_token_${appType}`)
    // 状态
    const token = ref<string | null>(null)
    const user = ref<User | null>(null)
    const enterprises = ref<Enterprise[]>([])
    const permissions = ref<string[]>([])
    const sourceFrom = ref<string | null>(null) // 来源标识状态

    // 权限管理状态
    const userPermissions = ref<MenuPermission[]>([])
    /**
     * 当前用户可用于菜单 / 按钮校验的权限码（category:shape:point）。
     * 租户端：来自角色权限管理接口；客户端：来自客商角色菜单权限。
     */
    const rolePermissionCodes = ref<string[]>([])
    const permissionsLoaded = ref(false)
    const routesAdded = ref(false)
    // 计算属性
    const isAuthenticated = computed(() => !!token.value && !!user.value)
    const isAdmin = computed(() => user.value?.userType === 1)
    const userInfo = computed(() => user.value)
    const availableEnterprises = computed(() => enterprises.value)
    const adminRole = computed(() => user.value.roleType === 1)

    /**
     * 将值转换为安全的字符串，处理undefined或null的情况
     * 防止出现 "undefined" 或 "null" 字符串
     */
    const toSafeString = (val: any) => (val === null || val === undefined || String(val) === 'undefined' || String(val) === 'null' ? '' : String(val))

    /**
     * 调用用户详情接口，写入 store 并同步浩客 identify（登录 / 资料变更时调用）
     */
    const refreshHowxmIdentify = async() => {
      if (!user.value?.id || !token.value) {
        return
      }

      try {
        const detail = isClient()
          ? await getClientUserDetail()
          : await getTenantUserDetail()
        const extras = buildHowxmIdentifyExtras(detail)

        user.value = {
          ...user.value,
          tenantId: extras.tenantId || user.value.tenantId,
          tenantName: extras.tenantName,
          branchId: extras.branchId,
          branchName: extras.branchName,
          ...(detail.enterpriseId ? { enterpriseId: toSafeString(detail.enterpriseId) } : {})
        }

        syncHowxmIdentify(user.value, extras)
        syncTrackingIdentify(user.value, extras)
      } catch (error) {
        console.warn('[CT_AUTH] 浩客 identify 用户详情获取失败，使用本地用户信息:', error)
        syncHowxmIdentify(user.value)
        syncTrackingIdentify(user.value)
      }
    }

    /**
     * 用 store 中已持久化的用户身份同步浩客（刷新页面时调用，不再请求 user/detail）
     */
    const syncHowxmIdentifyFromStore = () => {
      if (!user.value?.id || !token.value) {
        return
      }

      const hasHowxmContext = !!(user.value.tenantName || user.value.branchId)
      if (!hasHowxmContext) {
        void refreshHowxmIdentify()
        return
      }

      syncHowxmIdentify(user.value)
      syncTrackingIdentify(user.value)
    }

    /**
     * 处理登录响应的通用逻辑
     * @param response 登录响应数据
     */
    const handleLoginResponse = (response: any) => {
      resetShokzFeatureCache()
      resetUnifiedOrderVersionConfigCache()
      // 处理 token
      let tokenValue = null
      if (typeof response.token === 'string' && response.token) {
        tokenValue = response.token
        token.value = tokenValue
        tokenManager.set(tokenValue, 7)
      }

      // 设置用户信息
      user.value = {
        id: toSafeString(response.id),
        username: response.username,
        email: response.email || '',
        phone: response.phone,
        realName: response.realName,
        tenantId: toSafeString(response.tenantId),
        enterpriseId: toSafeString(response.enterpriseId),
        userType: response.userType,
        roleType: response.roleType
      }

      // 设置企业信息
      if (response.enterprises) {
        enterprises.value = response.enterprises
      }

      console.log(`[CT_AUTH] ${t('auth.store.loginSuccess')}:`, user.value.realName)
      console.log(`[CT_AUTH] ${t('auth.store.tokenStored')}`)

      void refreshHowxmIdentify()

      // 登录成功后权限会在路由跳转时自动加载，无需手动重置
      return response
    }

    /**
     * 用户登录（兼容现有接口）
     * @param credentials 登录凭据
     */
    const login = async(credentials: LoginCredentials) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.loginStart')}:`, credentials.account)
        const content = await tenantUserLogin(credentials)
        if (content) {
          return handleLoginResponse(content)
        }
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginFailed')}:`, error)
        throw error
      }
    }

    /**
     * 用户密码登录（新API接口）
     * @param params 登录参数
     */
    const passwordLogin = async(params: LoginRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.passwordLoginStart')}:`, params.account)
        const response = await userLogin(params)
        return handleLoginResponse(response)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginFailed')}:`, error)
        throw error
      }
    }

    /**
     * 验证码登录
     * @param params 验证码登录参数
     */
    const verifyCodeLogin = async(params: LoginVerifyRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.codeLoginStart')}:`, params.account)
        const response = await userLoginVerify(params)
        return handleLoginResponse(response)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginFailed')}:`, error)
        throw error
      }
    }

    /**
     * 注册发送验证码
     * @param params 手机号参数
     */
    const sendRegisterVerificationCode = async(params: RegisterCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.registerCodeSent')}:`, params.phone)
        const response = await sendRegisterCode(params)
        return response
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.registerCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 用户注册
     * @param params 注册参数
     */
    const register = async(params: RegisterRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.registerStart')}:`, params.username)
        const response = await userRegister(params)
        console.log(`[CT_AUTH] ${t('auth.store.registerSuccess')}`)
        return response
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.registerFailed')}:`, error)
        throw error
      }
    }

    /**
     * 登录发送验证码
     * @param params 账号参数
     */
    const sendLoginVerificationCode = async(params: LoginCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.loginCodeSent')}:`, params.account)
        return await sendLoginCode(params)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 密码重置发送验证码
     * @param params 账号参数
     */
    const sendPasswordResetVerificationCode = async(params: PasswordResetCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.resetCodeSent')}:`, params.account)
        return await sendPasswordResetCode(params)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.resetCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 重置密码
     * @param params 重置密码参数
     */
    const resetUserPassword = async(params: PasswordResetRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.resetPasswordStart')}:`, params.account)
        const response = await resetPassword(params)
        console.log(`[CT_AUTH] ${t('auth.store.resetPasswordSuccess')}`)
        return response
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.resetPasswordFailed')}:`, error)
        throw error
      }
    }

    /**
     * 用户登出
     */
    const logout = async() => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.logoutStart')}`)
        await userLogout()

        // 精确清除应用相关的缓存数据
        const appType = getAppType()

        // 清除当前应用类型的tabs缓存
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith(`controlTowerTabs_${appType}_`)) {
            sessionStorage.removeItem(key)
          }
        })

        // 清除当前用户的所有 UserStorage 数据
        const userStorageCount = UserStorage.clear()
        console.log(`[CT_AUTH] 清除用户存储数据: ${userStorageCount} 条`)

        // 重置所有状态（包括用户信息、权限、tabs等）
        resetShokzFeatureCache()
      resetUnifiedOrderVersionConfigCache()
        resetState()
        resetTracking()

        console.log(`[CT_AUTH] ${t('auth.store.logoutSuccess')}`)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.logoutFailed')}:`, error)
        // 即使API调用失败，也要清除本地状态
        UserStorage.clear()
        resetShokzFeatureCache()
      resetUnifiedOrderVersionConfigCache()
        resetState()
      }
    }

    /**
     * 检查用户权限
     * @param permission 权限字符串
     */
    const checkPermission = (permission: string): boolean => {
      if (!user.value) return false
      if (isAdmin.value) return true

      // TODO: 根据实际业务需求实现权限检查逻辑
      return true
    }

    /**
     * 检查用户角色
     * @param role 角色类型
     */
    const hasRole = (role: number | number[]): boolean => {
      if (!user.value) return false

      const roles = Array.isArray(role) ? role : [role]
      return roles.includes(user.value.roleType)
    }

    /**
     * 清除tabs缓存的方法引用
     * 由MainLayout组件设置，用于在退出登录时清除tabs数据
     */
    let clearTabsCache: (() => void) | null = null

    /**
     * 设置清除tabs缓存的方法
     * @param clearFn 清除tabs缓存的函数
     */
    const setClearTabsCache = (clearFn: () => void) => {
      clearTabsCache = clearFn
    }

    /**
     * 重置所有状态（包括权限状态）
     * 用于退出登录时清空所有数据
     */
    const resetState = () => {
      // 清除 Pinia store (会自动清除 localStorage)
      token.value = null
      user.value = null
      enterprises.value = []
      permissions.value = []

      // 清除权限管理状态（确保重新加载）
      userPermissions.value = []
      rolePermissionCodes.value = []
      permissionsLoaded.value = false
      routesAdded.value = false

      // 清除tabs缓存
      if (clearTabsCache) {
        clearTabsCache()
      }

      // 同时清除 cookies
      tokenManager.remove()

      console.log(`[CT_AUTH] ${t('auth.store.authStateReset')}`)
      console.log(`[CT_AUTH] ${t('auth.store.tokenCleared')}`)
      console.log(`[CT_AUTH] ${t('auth.store.tabsCacheCleared')}`)
    }

    /**
     * 加载用户权限
     */
    const loadUserPermissions = async(): Promise<MenuPermission[]> => {
      try {
        userPermissions.value = await getMenuPermissions(user.value?.userType)
        // 租户端：合并角色功能权限（v-permission / Tab 控制依赖此项）
        if (user.value?.userType === UserType.TENANT) {
          try {
            rolePermissionCodes.value = await getCurrentUserRolePrivileges()
          } catch (roleErr) {
            console.warn('[CT_AUTH] 角色权限加载失败，仅使用菜单权限:', roleErr)
            rolePermissionCodes.value = []
          }
        } else {
          // 客户端没有租户角色权限接口；客商中心配的角色权限已由 menuPrivilege 算完
          rolePermissionCodes.value = (userPermissions.value || [])
            .filter(p => p.status === 1 && p.permissionCode)
            .map(p => p.permissionCode)
        }
        permissionsLoaded.value = true
        return userPermissions.value
      } catch (error) {
        console.error('权限加载失败:', error)
        throw error
      }
    }

    /**
     * 检查用户是否有指定权限
     */
    const hasPermission = (permission: string): boolean => {
      if (adminRole.value) {
          return true
      }
      return rolePermissionCodes.value.includes(permission)
    }

    /**
     * 检查权限是否已显式分配到当前租户菜单
     * 不因租户管理员角色而绕过，用于必须由租户授权的功能。
     */
    const hasExplicitMenuPermission = (permission: string): boolean => {
      if (!user.value) return false
      return userPermissions.value.some(item =>
        item.status === 1 && item.permissionCode === permission
      )
    }

    /**
     * 重置权限状态
     */
    const resetPermissions = () => {
      userPermissions.value = []
      rolePermissionCodes.value = []
      permissionsLoaded.value = false
      routesAdded.value = false
    }

    /**
     * 设置路由已添加状态
     */
    const setRoutesAdded = (value: boolean) => {
      routesAdded.value = value
    }

    /**
     * 更新用户信息
     * @param newUserInfo 新的用户信息
     */
    const updateUserInfo = (newUserInfo: Partial<User>) => {
      if (user.value) {
        user.value = { ...user.value, ...newUserInfo }
        console.log(`[CT_AUTH] ${t('auth.store.userInfoUpdated')}`)
        void refreshHowxmIdentify()
      }
    }

    // ==================== 客户端认证方法 ====================

    /**
     * 客户端用户密码登录
     * @param params 登录参数
     */
    const clientPasswordLogin = async(params: LoginRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.passwordLoginStart')}:`, params.account)
        const response = await clientUserLogin(params)
        return handleLoginResponse(response)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端验证码登录
     * @param params 验证码登录参数
     */
    const clientVerifyCodeLogin = async(params: LoginVerifyRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.codeLoginStart')}:`, params.account)
        const response = await clientUserLoginVerify(params)
        return handleLoginResponse(response)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端注册发送验证码
     * @param params 手机号参数
     */
    const clientSendRegisterVerificationCode = async(params: RegisterCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.registerCodeSent')}:`, params.phone)
        return await clientSendRegisterCode(params)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.registerCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端用户注册
     * @param params 注册参数
     */
    const clientRegister = async(params: RegisterRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.registerStart')}:`, params.username)
        const response = await clientUserRegister(params)
        return handleLoginResponse(response)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.registerFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端登录发送验证码
     * @param params 账号参数
     */
    const clientSendLoginVerificationCode = async(params: LoginCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.loginCodeSent')}:`, params.account)
        return await clientSendLoginCode(params)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.loginCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端密码重置发送验证码
     * @param params 账号参数
     */
    const clientSendPasswordResetVerificationCode = async(params: PasswordResetCodeRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.resetCodeSent')}:`, params.account)
        return await clientSendPasswordResetCode(params)
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.resetCodeFailed')}:`, error)
        throw error
      }
    }

    /**
     * 客户端重置密码
     * @param params 重置密码参数
     */
    const clientResetUserPassword = async(params: PasswordResetRequest) => {
      try {
        console.log(`[CT_AUTH] ${t('auth.store.resetPasswordStart')}:`, params.account)
        const response = await clientResetPassword(params)
        console.log(`[CT_AUTH] ${t('auth.store.resetPasswordSuccess')}`)
        return response
      } catch (error) {
        console.error(`[CT_AUTH] ${t('auth.store.resetPasswordFailed')}:`, error)
        throw error
      }
    }

    /**
     * 直接设置用户信息（用于兼容现有代码）
     * @param userData 用户数据
     */
    const setUser = (userData: Partial<User>) => {
      if (userData) {
        // 转换数据格式以确保兼容性
        user.value = {
          id: toSafeString(userData.id),
          username: userData.username || '',
          email: userData.email || '',
          phone: userData.phone || '',
          realName: userData.realName || userData.username || '',
          tenantId: toSafeString(userData.tenantId),
          enterpriseId: toSafeString(userData.enterpriseId),
          userType: userData.userType || 0,
          roleType: userData.roleType || 0
        }
        console.log(`[CT_AUTH] ${t('auth.store.userInfoUpdated')}`)
        void refreshHowxmIdentify()
      }
    }

    /**
     * Code自动登录
     * @param code 6位验证码
     */
    const autoLoginWithCode = async(code: string) => {
      try {
        console.log('[CT_AUTH] Code自动登录开始:', code)

        // 1. 通过code获取token和用户类型
        const response: ConvertCodeToTokenResponse = await convertCodeToToken(code)

        // 检查响应是否为null（token获取失败）
        if (!response) {
          console.warn('[CT_AUTH] convertCodeToToken返回null，token获取失败')
          return null
        }

        const { token: tokenValue, userType: responseUserType } = response

        console.log('[CT_AUTH] 从接口获取用户类型:', responseUserType)

        // 2. 设置token到store和cookies
        token.value = tokenValue
        tokenManager.set(token.value, 7)

        // 3. 根据接口返回的用户类型获取用户信息
        let userDetail: any
        if (responseUserType === UserType.CLIENT) {
          userDetail = await getClientUserDetail()
          console.log('[CT_AUTH] 使用客户端用户详情接口')
        } else {
          userDetail = await getTenantUserDetail()
          console.log('[CT_AUTH] 使用租户用户详情接口')
        }

        // 4. 设置用户信息
        const howxmExtras = buildHowxmIdentifyExtras(userDetail)
        user.value = {
          id: toSafeString(userDetail.id),
          username: userDetail.username,
          email: userDetail.email || '',
          phone: userDetail.phone,
          realName: userDetail.realName,
          tenantId: howxmExtras.tenantId || toSafeString(userDetail.tenantId),
          tenantName: howxmExtras.tenantName,
          branchId: howxmExtras.branchId,
          branchName: howxmExtras.branchName,
          enterpriseId: toSafeString(userDetail.enterpriseId),
          userType: responseUserType,
          roleType: userDetail.roleType
        }

        // 5. 设置企业信息（如果有）
        if (userDetail.enterprises) {
          enterprises.value = userDetail.enterprises
        }

        console.log('[CT_AUTH] Code自动登录成功:', user.value.realName)
        syncHowxmIdentify(user.value, howxmExtras)

        // Code自动登录成功后权限会在路由跳转时自动加载，无需手动重置
        return userDetail
      } catch (error) {
        console.error('[CT_AUTH] Code自动登录失败:', error)
        throw error
      }
    }

    /**
     * 获取返回门户的Code
     */
    const getReturnCode = async(): Promise<string> => {
      try {
        console.log('[CT_AUTH] 获取返回门户Code')
        const code = await convertTokenToCode()
        console.log('[CT_AUTH] 获取Code成功:', code)
        return code
      } catch (error) {
        console.error('[CT_AUTH] 获取Code失败:', error)
        throw error
      }
    }

    /**
     * 根据来源获取返回URL
     *
     * 对于portal来源，动态生成URL避免硬编码
     * 例如：https://controltower-tenant-stage.walltechsystem.com/portal
     *
     * @returns {string} 返回的目标URL
     */
    const getReturnUrl = (): string => {
      const source = sourceFrom.value

      if (source === 'home') {
        return import.meta.env.VITE_HOME_URL
      }

      if (source === 'portal') {
        // 根据当前域名动态生成portal URL
        const currentOrigin = window.location.origin
        return `${currentOrigin}/portal`
      }

      // 默认返回home地址
      return import.meta.env.VITE_HOME_URL
    }

    /**
     * 设置来源标识
     * @param source 来源标识
     */
    const setSourceFrom = (source: string | null) => {
      sourceFrom.value = source
    }

    /**
     * 清除来源标识
     */
    const clearSourceFrom = () => {
      sourceFrom.value = null
    }

    return {
      token,
      user,
      enterprises,
      permissions,
      sourceFrom,

      // 计算属性
      isAuthenticated,
      isAdmin,
      adminRole,
      userInfo,
      availableEnterprises,

      // 操作方法 - 租户端
      login, // 兼容现有接口
      passwordLogin, // 新密码登录接口
      verifyCodeLogin, // 验证码登录
      register, // 用户注册
      sendRegisterVerificationCode, // 发送注册验证码
      sendLoginVerificationCode, // 发送登录验证码
      sendPasswordResetVerificationCode, // 发送密码重置验证码
      resetUserPassword, // 重置密码

      // 操作方法 - 客户端
      clientPasswordLogin, // 客户端密码登录
      clientVerifyCodeLogin, // 客户端验证码登录
      clientRegister, // 客户端用户注册
      clientSendRegisterVerificationCode, // 客户端发送注册验证码
      clientSendLoginVerificationCode, // 客户端发送登录验证码
      clientSendPasswordResetVerificationCode, // 客户端发送密码重置验证码
      clientResetUserPassword, // 客户端重置密码

      // 通用操作方法
      logout,
      checkPermission,
      hasRole,
      resetState,
      updateUserInfo,
      setUser, // 直接设置用户信息
      autoLoginWithCode, // 新增的Code自动登录方法
      getReturnCode, // 新增的获取返回门户Code方法
      getReturnUrl, // 新增的根据来源获取返回URL方法
      setSourceFrom, // 新增的设置来源标识方法
      clearSourceFrom, // 新增的清除来源标识方法
      refreshHowxmIdentify,
      syncHowxmIdentifyFromStore,
      handleLoginResponse, // 新增暴露
      setClearTabsCache, // 设置清除tabs缓存的方法

      // 权限管理方法
      loadUserPermissions,
      hasPermission,
      hasExplicitMenuPermission,
      resetPermissions,
      setRoutesAdded,

      // 权限状态
      userPermissions: readonly(userPermissions),
      rolePermissionCodes: readonly(rolePermissionCodes),
      permissionsLoaded: readonly(permissionsLoaded),
      routesAdded: readonly(routesAdded)
    }
  },
  {
    persist: {
      storage: localStorage,
      paths: [
        'token',
        'user',
        'enterprises',
        'permissions',
        'sourceFrom'
        // 权限相关状态每次刷新重新拉取，且对外 readonly，不可持久化（否则会 hydration 失败）
      ],
      key: `ct_auth_store_${getAppType()}`
    }
  }
)
